import React, { useState, useRef, useEffect, useCallback } from "react";
import "./VideoReviewSection.css";

const API_BASE = process.env.REACT_APP_API_URL || "https://vedonica.in/api";
const AUTO_SLIDE_INTERVAL = 3500;

const StarRating = ({ count }) => (
  <div className="vrs-stars">
    {Array.from({ length: 5 }).map((_, i) => (
      <i key={i} className={i < count ? "fa-solid fa-star" : "fa-regular fa-star"} />
    ))}
  </div>
);

function VideoCard({ review, onZoom, onUnmute }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [loading, setLoading] = useState(false);

  const togglePlay = useCallback((e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      setLoading(true);
      v.play()
        .then(() => setLoading(false))
        .catch(() => setLoading(false));
    } else {
      v.pause();
    }
  }, []);

  // ✅ FIX: Jab koi video unmute ho, onUnmute callback se pehli wali mute ho jaayegi
  const toggleMute = useCallback((e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    const newMuted = !v.muted;
    v.muted = newMuted;
    setMuted(newMuted);
    if (!newMuted) {
      onUnmute(v); // Pehle wali unmuted video ko mute karega
    }
  }, [onUnmute]);

  const handleCardClick = useCallback(
    (e) => {
      if (e.target.closest("button")) return;
      onZoom(review);
    },
    [review, onZoom]
  );

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const handlePlay = () => setPlaying(true);
    const handlePause = () => setPlaying(false);
    v.addEventListener("play", handlePlay);
    v.addEventListener("pause", handlePause);
    return () => {
      v.removeEventListener("play", handlePlay);
      v.removeEventListener("pause", handlePause);
    };
  }, []);

  // ✅ FIX: Parent se mute state sync karne ke liye — agar activeVideoRef change ho
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const syncMute = () => setMuted(v.muted);
    v.addEventListener("volumechange", syncMute);
    return () => v.removeEventListener("volumechange", syncMute);
  }, []);

  return (
    <div className="vrs-card" onClick={handleCardClick}>
      <div className="vrs-card__media">
        <video
          ref={videoRef}
          src={review.video_url}
          loop
          muted
          playsInline
          preload="metadata"
          className="vrs-card__video"
        />

        {loading && (
          <div className="vrs-card__spinner">
            <i className="fa-solid fa-spinner fa-spin" />
          </div>
        )}

        {!playing && !loading && (
          <button
            className="vrs-card__play-btn"
            onClick={togglePlay}
            aria-label="Play video"
            type="button"
          >
            <i className="fa-solid fa-play" />
          </button>
        )}

        {playing && (
          <button
            className="vrs-card__pause-btn"
            onClick={togglePlay}
            aria-label="Pause video"
            type="button"
          >
            <i className="fa-solid fa-pause" />
          </button>
        )}

        <button
          className="vrs-card__mute-btn"
          onClick={toggleMute}
          aria-label={muted ? "Unmute" : "Mute"}
          type="button"
        >
          <i className={muted ? "fa-solid fa-volume-xmark" : "fa-solid fa-volume-high"} />
        </button>

        <button
          className="vrs-card__expand-btn"
          onClick={(e) => {
            e.stopPropagation();
            onZoom(review);
          }}
          aria-label="Expand"
          type="button"
        >
          <i className="fa-solid fa-expand" />
        </button>

        <div className="vrs-card__overlay">
          <p className="vrs-card__name">{review.reviewer_name}</p>
          <StarRating count={review.rating} />
          <p className="vrs-card__product">{review.product_name}</p>
          <p className="vrs-card__duration">
            <i className="fa-regular fa-clock" /> {review.usage_duration}
          </p>
        </div>
      </div>
    </div>
  );
}

function ZoomModal({ review, onClose, onMuteActiveCard }) {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(false);
  const [playing, setPlaying] = useState(true);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !review) return;

    // ✅ FIX: Modal khulle to card wali video mute ho jaaye
    if (onMuteActiveCard) onMuteActiveCard();

    v.muted = false;
    v.play().catch(() => {});

    const handlePlay = () => setPlaying(true);
    const handlePause = () => setPlaying(false);
    v.addEventListener("play", handlePlay);
    v.addEventListener("pause", handlePause);

    return () => {
      v.pause();
      v.removeEventListener("play", handlePlay);
      v.removeEventListener("pause", handlePause);
    };
  }, [review]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const toggleMute = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  }, []);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.paused ? v.play() : v.pause();
  }, []);

  if (!review) return null;

  return (
    <div
      className="vrs-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="vrs-modal__box">
        <button
          className="vrs-modal__close"
          onClick={onClose}
          aria-label="Close"
          type="button"
        >
          <i className="fa-solid fa-xmark" />
        </button>

        <div className="vrs-modal__media">
          <video
            ref={videoRef}
            src={review.video_url}
            loop
            playsInline
            className="vrs-modal__video"
          />
          <div className="vrs-modal__vid-controls">
            <button onClick={togglePlay} aria-label={playing ? "Pause" : "Play"} type="button">
              <i className={playing ? "fa-solid fa-pause" : "fa-solid fa-play"} />
            </button>
            <button onClick={toggleMute} aria-label={muted ? "Unmute" : "Mute"} type="button">
              <i className={muted ? "fa-solid fa-volume-xmark" : "fa-solid fa-volume-high"} />
            </button>
          </div>
        </div>

        <div className="vrs-modal__info">
          <div className="vrs-modal__avatar">
            {review.reviewer_name?.charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="vrs-modal__name">{review.reviewer_name}</p>
            <StarRating count={review.rating} />
            <p className="vrs-modal__product">
              <i className="fa-solid fa-leaf" /> {review.product_name}
            </p>
            <p className="vrs-modal__duration">
              <i className="fa-regular fa-clock" /> {review.usage_duration}
            </p>
            {review.comment && (
              <p className="vrs-modal__comment">"{review.comment}"</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function VideoReviewSection() {
  const trackRef = useRef(null);
  const outerRef = useRef(null);
  const intervalRef = useRef(null);
  const isHoveredRef = useRef(false);
  const touchStartRef = useRef(null);

  // ✅ FIX: Currently unmuted card video ka ref yahan store hoga
  const activeVideoRef = useRef(null);

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [visCount, setVisCount] = useState(3);
  const [zoomedReview, setZoomedReview] = useState(null);

  // ✅ FIX: Jab bhi koi card unmute ho — pehle wala mute karo, naya store karo
  const handleUnmute = useCallback((videoEl) => {
    if (activeVideoRef.current && activeVideoRef.current !== videoEl) {
      activeVideoRef.current.muted = true;
    }
    activeVideoRef.current = videoEl;
  }, []);

  // ✅ FIX: Modal khulle to active card video mute ho jaaye
  const handleMuteActiveCard = useCallback(() => {
    if (activeVideoRef.current) {
      activeVideoRef.current.muted = true;
      activeVideoRef.current = null;
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);

    fetch(`${API_BASE}/products/video-reviews/`, { signal: controller.signal })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        setReviews(Array.isArray(data) ? data : data.results || []);
        setLoading(false);
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          setError("Could not load reviews.");
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, []);

  const maxIdx = Math.max(0, reviews.length - visCount);

  const stopAutoSlide = useCallback(() => {
    clearInterval(intervalRef.current);
  }, []);

  const startAutoSlide = useCallback(() => {
    stopAutoSlide();
    intervalRef.current = setInterval(() => {
      if (!isHoveredRef.current) {
        setCurrentIdx((prev) => (prev >= maxIdx ? 0 : prev + 1));
      }
    }, AUTO_SLIDE_INTERVAL);
  }, [maxIdx, stopAutoSlide]);

  useEffect(() => {
    if (reviews.length > 0) startAutoSlide();
    return stopAutoSlide;
  }, [reviews.length, startAutoSlide, stopAutoSlide]);

  useEffect(() => {
    const updateVis = () => {
      const w = outerRef.current ? outerRef.current.offsetWidth : window.innerWidth;
      setVisCount(w < 480 ? 1 : w < 768 ? 2 : 3);
    };
    updateVis();
    window.addEventListener("resize", updateVis);
    return () => window.removeEventListener("resize", updateVis);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track || !track.firstChild) return;
    const gap = 14;
    const cardW = track.firstChild.offsetWidth + gap;
    track.style.transform = `translateX(-${currentIdx * cardW}px)`;
  }, [currentIdx, visCount]);

  const handleNav = useCallback(
    (idx) => {
      setCurrentIdx(Math.max(0, Math.min(idx, maxIdx)));
      stopAutoSlide();
      startAutoSlide();
    },
    [maxIdx, stopAutoSlide, startAutoSlide]
  );

  const handleTouchStart = (e) => {
    touchStartRef.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartRef.current === null) return;
    const diff = touchStartRef.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      diff > 0 ? handleNav(currentIdx + 1) : handleNav(currentIdx - 1);
    }
    touchStartRef.current = null;
  };

  return (
    <section className="vrs-section">
      <link
        rel="stylesheet"
        href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css"
        crossOrigin="anonymous"
      />

      <div className="vrs-container">
        <div className="vrs-header">
          <span className="vrs-header__tag">
            <i className="fa-solid fa-video" /> Video Reviews
          </span>
          <h4 className="vrs-heading">Customer Video Reviews</h4>
          <p className="vrs-subheading">Real customers, real Ayurvedic results</p>
        </div>

        {loading && (
          <div className="vrs-state">
            <i className="fa-solid fa-spinner fa-spin vrs-state__icon" />
            <p>Loading reviews...</p>
          </div>
        )}

        {error && !loading && (
          <div className="vrs-state vrs-state--error">
            <i className="fa-solid fa-circle-exclamation vrs-state__icon" />
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && reviews.length === 0 && (
          <div className="vrs-state">
            <i className="fa-solid fa-video-slash vrs-state__icon" />
            <p>No reviews yet.</p>
          </div>
        )}

        {!loading && !error && reviews.length > 0 && (
          <>
            <div
              className="vrs-slider-wrap"
              onMouseEnter={() => { isHoveredRef.current = true; }}
              onMouseLeave={() => { isHoveredRef.current = false; }}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              <button
                className="vrs-nav vrs-nav--prev"
                onClick={() => handleNav(currentIdx - 1)}
                disabled={currentIdx === 0}
                aria-label="Previous slide"
                type="button"
              >
                <i className="fa-solid fa-chevron-left" />
              </button>

              <div className="vrs-outer" ref={outerRef}>
                <div className="vrs-track" ref={trackRef}>
                  {reviews.map((review) => (
                    <VideoCard
                      key={review.id}
                      review={review}
                      onZoom={setZoomedReview}
                      onUnmute={handleUnmute}  
                    />
                  ))}
                </div>
              </div>

              <button
                className="vrs-nav vrs-nav--next"
                onClick={() => handleNav(currentIdx + 1)}
                disabled={currentIdx >= maxIdx}
                aria-label="Next slide"
                type="button"
              >
                <i className="fa-solid fa-chevron-right" />
              </button>
            </div>

            <div className="vrs-dots">
              {Array.from({ length: maxIdx + 1 }).map((_, i) => (
                <button
                  key={i}
                  className={`vrs-dot${i === currentIdx ? " vrs-dot--active" : ""}`}
                  onClick={() => handleNav(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  type="button"
                />
              ))}
            </div>
          </>
        )}
      </div>

      {zoomedReview && (
        <ZoomModal
          review={zoomedReview}
          onClose={() => setZoomedReview(null)}
          onMuteActiveCard={handleMuteActiveCard} 
        />
      )}
    </section>
  );
}

export default VideoReviewSection;